import spacetime from 'spacetime';
import type { Dispatch, SetStateAction } from 'react';

import { tzMoscow } from 'CorePackage/MainModule/constants/timeZone';
import { IKnmPlan } from 'PlanListPackage/MainModule/interfaces/IKnmPlan';
import { ErknmPlanStatusesEnum } from 'APIPackage/PlanListModule/enums/ErknmPlanStatusesEnum';
import { INotification } from 'CorePackage/NotificationModule/interfaces/INotification';
import {
  ERKNM_PLAN_HAS_DISAPPROVED_KNM_ERROR,
  ERKNM_PLAN_UNDER_APPROVED_LATE_ERROR,
  ERKNM_PLAN_UNDER_CONSIDERATION_LATE_ERROR,
  ERKNM_PLAN_UNDER_CONSIDERED_LATE_ERROR,
  FILL_KNM_PASSPORTS,
  KNMS_HAVE_IN_PROCESS_MESSAGE,
  KNMS_NOT_IN_STATUS_MESSAGE,
  PLAN_CONSIDERED_ERROR_MESSAGE,
  PLAN_STATUS_UPDATE_TO_APPROVED_MESSAGE,
  ERKNM_PLAN_NOT_FILLED_SIGNER_INFO_ERROR,
} from 'CorePackage/MainModule/constants/notices';
import { NoticeTypesEnum } from 'CorePackage/NotificationModule/enums/NoticeTypesEnum';
import { MessageTypesEnum } from 'CorePackage/NotificationModule/enums/MessageTypesEnum';
import { IKnmPlanInfo } from 'APIPackage/PlanListModule/interfaces/IKnmPlanInfo';
import { ChangePlanStatusTypesEnum } from 'CorePackage/ChangePlanStatusModalsModule/enums/ChangePlanStatusTypesEnum';
import { getPlanErknmStatusTitleByStatus } from 'APIPackage/PlanListModule/common/getPlanErknmStatusTitleByStatus';
import { isLaterThanAdminSetting } from 'CorePackage/MainModule/validators/isLaterThanAdminSetting';
import { IAdminSetting } from 'APIPackage/AdminModule/interfaces/IAdminSetting';
import { AdminSettingCodesEnum } from 'APIPackage/AdminModule/enums/AdminSettingCodesEnum';

import { IPlanFormRef } from '../interfaces/IPlanFormRef';

interface IPlanStatusInfo {
  id: number;
  newStatus: ErknmPlanStatusesEnum;
  knmAmount: number;
}

interface IChangePlanStatus {
  plan: IKnmPlan | undefined;
  newStatus: ErknmPlanStatusesEnum;
  isProsecutorOrSuperProsecutor: boolean;
  planInfo: IKnmPlanInfo | undefined;
  isPrivilegesKno: boolean | undefined;
  addNotice: (notice: INotification) => void;
  setPlanStatusInfo: Dispatch<SetStateAction<IPlanStatusInfo | null>>;
  setActiveModal: (value: SetStateAction<ChangePlanStatusTypesEnum | null>) => void;
  setErrorTextForModal?: (value: SetStateAction<string | undefined>) => void;
  settings: IAdminSetting[] | undefined;
  formRef: IPlanFormRef | undefined;
}

export const changePlanStatus = ({
  plan,
  newStatus,
  isProsecutorOrSuperProsecutor,
  planInfo,
  isPrivilegesKno,
  addNotice,
  setPlanStatusInfo,
  setActiveModal,
  setErrorTextForModal,
  settings,
  formRef,
}: IChangePlanStatus) => {
  const totalKnm = planInfo?.totalKnm || plan?.knmAmount;
  const fillInProcessKnm = planInfo?.fillInProcessKnm;
  const userDate = spacetime.now(tzMoscow);
  const currentPlanYear = plan?.startDate ? new Date(plan?.startDate).getFullYear() - 1 : new Date().getFullYear();

  const planConsiderationLastDayOfYearSettingValue = settings?.find(
    item => item.code === AdminSettingCodesEnum.PLAN_CONSIDERATION_LAST_DAY_OF_YEAR,
  )?.value;
  const planConsiderationLastDayOfYear = planConsiderationLastDayOfYearSettingValue
    ? spacetime(planConsiderationLastDayOfYearSettingValue, tzMoscow).year(currentPlanYear).hour(23).minute(59)
    : spacetime(`November 3 ${currentPlanYear} 23:59`, tzMoscow);

  const isFioAndJobTitle414 = isLaterThanAdminSetting(
    plan?.created,
    settings,
    AdminSettingCodesEnum.PLAN_SIGNER_166_4_1_4_START_FROM,
  );

  if (isFioAndJobTitle414) {
    if (formRef) {
      const isValidationErrors = formRef?.current.form.getState()?.hasValidationErrors;

      if (isValidationErrors) {
        formRef.current?.form.submit();

        return;
      }
    } else if (!plan?.knmPlanData?.signerFullName && !plan?.knmPlanData?.signerPosition) {
      addNotice({
        renderContent: ERKNM_PLAN_NOT_FILLED_SIGNER_INFO_ERROR,
        noticeType: NoticeTypesEnum.ERROR,
        messageType: MessageTypesEnum.PLAN_SAVE_ERROR,
      });

      return;
    }
  }

  const allowedPlanIdsForApprovedStatusSettingValue = settings?.find(
    item => item.code === AdminSettingCodesEnum.ALLOWED_PLAN_IDS_FOR_APPROVED_STATUS,
  )?.value;
  const allowedPlanIdsForApprovedStatus: number[] = allowedPlanIdsForApprovedStatusSettingValue
    ? JSON.parse(allowedPlanIdsForApprovedStatusSettingValue)
    : [];
  const allowedPlanIdsForUnderConsiderationStatusSettingValue = settings?.find(
    item => item.code === AdminSettingCodesEnum.ALLOWED_PLAN_IDS_FOR_UNDER_CONSIDERATION_STATUS,
  )?.value;
  const allowedPlanIdsForUnderConsiderationStatus: number[] = allowedPlanIdsForUnderConsiderationStatusSettingValue
    ? JSON.parse(allowedPlanIdsForUnderConsiderationStatusSettingValue)
    : [];

  if (
    newStatus === ErknmPlanStatusesEnum.UNDER_CONSIDERATION &&
    userDate.isAfter(spacetime(`October 1 ${currentPlanYear} 23:00`, tzMoscow)) &&
    !isPrivilegesKno &&
    plan &&
    !allowedPlanIdsForUnderConsiderationStatus.includes(plan.id)
  ) {
    addNotice({
      renderContent: ERKNM_PLAN_UNDER_CONSIDERATION_LATE_ERROR,
      noticeType: NoticeTypesEnum.ERROR,
      messageType: MessageTypesEnum.CHANGE_STATUS_TIME_IS_OUT,
    });
  } else if (newStatus === ErknmPlanStatusesEnum.CONSIDERED && userDate.isAfter(planConsiderationLastDayOfYear)) {
    addNotice({
      renderContent: ERKNM_PLAN_UNDER_CONSIDERED_LATE_ERROR,
      noticeType: NoticeTypesEnum.ERROR,
      messageType: MessageTypesEnum.CHANGE_STATUS_TIME_IS_OUT,
    });
  } else if (
    newStatus === ErknmPlanStatusesEnum.APPROVED &&
    userDate.isAfter(spacetime(`December 15 ${currentPlanYear} 23:00`, tzMoscow)) &&
    plan &&
    !allowedPlanIdsForApprovedStatus.includes(plan.id)
  ) {
    addNotice({
      renderContent: ERKNM_PLAN_UNDER_APPROVED_LATE_ERROR,
      noticeType: NoticeTypesEnum.ERROR,
      messageType: MessageTypesEnum.CHANGE_STATUS_TIME_IS_OUT,
    });
  } else if (newStatus === ErknmPlanStatusesEnum.UNDER_CONSIDERATION && !totalKnm) {
    addNotice({
      renderContent: 'Пустой план перевести в следующий статус нельзя.',
      noticeType: NoticeTypesEnum.ERROR,
      messageType: MessageTypesEnum.PLAN_IS_EMPTY,
    });
  } else if (newStatus === ErknmPlanStatusesEnum.UNDER_CONSIDERATION && fillInProcessKnm) {
    addNotice({
      renderContent: KNMS_NOT_IN_STATUS_MESSAGE,
      noticeType: NoticeTypesEnum.ERROR,
      messageType: MessageTypesEnum.PLAN_IS_EMPTY,
    });
  } else if (
    plan &&
    // условия ниже не должно быть, удалить после того, как в списке планов будет корректно приходить knmPlanData
    (planInfo ? isProsecutorOrSuperProsecutor || plan.knmPlanData?.signedXlsxFile?.signed : true) &&
    (planInfo ? fillInProcessKnm === 0 : true) &&
    (newStatus !== ErknmPlanStatusesEnum.CONSIDERED || (planInfo ? planInfo.underConsiderationKnm === 0 : true))
  ) {
    const { id, disapprovedKnmAmount } = plan;

    setPlanStatusInfo({ id, newStatus, knmAmount: plan.knmAmount });
    if (newStatus === ErknmPlanStatusesEnum.APPROVED && disapprovedKnmAmount) {
      addNotice({
        renderContent: ERKNM_PLAN_HAS_DISAPPROVED_KNM_ERROR,
        noticeType: NoticeTypesEnum.WARNING,
        messageType: MessageTypesEnum.NEW_PLAN_STATUS_WITH_DISAPPROVED_KNMS,
      });
    }
    const changePlanStatusType: ChangePlanStatusTypesEnum =
      newStatus === ErknmPlanStatusesEnum.CONSIDERED
        ? ChangePlanStatusTypesEnum.TO_CONSIDER
        : ChangePlanStatusTypesEnum.PRIMARY;

    setActiveModal(changePlanStatusType);
  } else if (setErrorTextForModal) {
    if (plan && !plan.knmPlanData?.signedXlsxFile?.signed && !isProsecutorOrSuperProsecutor) {
      addNotice({
        renderContent: `Для перевода плана в статус "${getPlanErknmStatusTitleByStatus(
          newStatus,
        )}" необходимо его подписать`,
        noticeType: NoticeTypesEnum.ERROR,
        messageType: MessageTypesEnum.UPDATE_PLAN_STATUSES,
      });
    } else if (planInfo?.fillInProcessKnm) {
      if (newStatus === ErknmPlanStatusesEnum.UNDER_CONSIDERATION) {
        setErrorTextForModal(KNMS_NOT_IN_STATUS_MESSAGE);
      } else if (newStatus === ErknmPlanStatusesEnum.APPROVED) {
        setErrorTextForModal(PLAN_STATUS_UPDATE_TO_APPROVED_MESSAGE);
      } else {
        setErrorTextForModal(
          `Для перевода плана в статус "${getPlanErknmStatusTitleByStatus(newStatus)}" необходимо его подписать`,
        );
      }
      setActiveModal(ChangePlanStatusTypesEnum.ERROR);
    } else if (planInfo?.fillInProcessKnm || planInfo?.underConsiderationKnm) {
      if (newStatus === ErknmPlanStatusesEnum.UNDER_CONSIDERATION) {
        setErrorTextForModal(KNMS_HAVE_IN_PROCESS_MESSAGE);
      } else if (newStatus === ErknmPlanStatusesEnum.CONSIDERED) {
        setErrorTextForModal(PLAN_CONSIDERED_ERROR_MESSAGE);
      } else {
        setErrorTextForModal(FILL_KNM_PASSPORTS);
      }
      setActiveModal(ChangePlanStatusTypesEnum.ERROR);
    }
  }
};
